import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RealtimeUpdates = ({ onNewData }) => {
  const [updates, setUpdates] = useState([]);
  const [isConnected, setIsConnected] = useState(true);

  // Mock real-time updates
  useEffect(() => {
    const mockUpdates = [
      {
        id: 1,
        type: 'data',
        message: 'New survey data from ProposalBot: 247 responses processed',
        timestamp: new Date(Date.now() - 120000),
        icon: 'Database',
        color: 'success'
      },
      {
        id: 2,
        type: 'analysis',
        message: 'InsightMiner completed sentiment analysis for Q4 2024',
        timestamp: new Date(Date.now() - 300000),
        icon: 'Brain',
        color: 'primary'
      },
      {
        id: 3,
        type: 'export',
        message: 'Power BI export completed successfully',
        timestamp: new Date(Date.now() - 480000),
        icon: 'Download',
        color: 'warning'
      },
      {
        id: 4,
        type: 'agent',
        message: 'Efficiency Auditor started processing new dataset',
        timestamp: new Date(Date.now() - 600000),
        icon: 'Bot',
        color: 'secondary'
      }
    ];

    setUpdates(mockUpdates);

    // Simulate new updates
    const interval = setInterval(() => {
      const newUpdate = {
        id: Date.now(),
        type: 'data',
        message: `Real-time update: ${Math.floor(Math.random() * 100)} new data points collected`,
        timestamp: new Date(),
        icon: 'Zap',
        color: 'accent'
      };
      
      setUpdates(prev => [newUpdate, ...prev?.slice(0, 4)]);
      onNewData && onNewData(newUpdate);
    }, 30000);

    return () => clearInterval(interval);
  }, [onNewData]);

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return timestamp?.toLocaleDateString();
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      case 'accent': return 'text-accent';
      case 'secondary': return 'text-secondary';
      default: return 'text-primary';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Live Updates</h3>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success animate-pulse-soft' : 'bg-error'}`} />
          <span className="text-xs text-muted-foreground">
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {updates?.map((update) => (
          <div
            key={update?.id}
            className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors duration-200 animate-fade-in"
          >
            <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center flex-shrink-0">
              <Icon 
                name={update?.icon} 
                size={16} 
                className={getColorClass(update?.color)}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground mb-1">
                {update?.message}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatTime(update?.timestamp)}
              </p>
            </div>
          </div>
        ))}
      </div>
      {updates?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Clock" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Waiting for updates...
          </p>
        </div>
      )}
    </div>
  );
};

export default RealtimeUpdates;